/**
 * GraphQL Notification Service
 * Handles backend-persisted notifications via AppSync
 */

import { generateClient } from 'aws-amplify/api';
import { AppNotification, NotificationCategory, NotificationPriority } from '../types/notification';
import { collectAcrossIdentities } from './identity';

// `any`, as in leave.ts: the generated type sends TypeScript 5+ (the mobile app) into "excessive
// stack depth" (TS2321), and nothing here relies on it.
let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};
const graphqlExecutionCounts = new Map<string, number>();
const NOTIFICATION_LIST_CACHE_TTL_MS = 10000;

type NotificationListOptions = {
  force?: boolean;
};

type NotificationListCacheEntry = {
  fetchedAt: number;
  data?: AppNotification[];
  promise?: Promise<AppNotification[]>;
};

const notificationListCache = new Map<string, NotificationListCacheEntry>();

function getOperationName(query: unknown): string {
  const text = String(query || '');
  const match = text.match(/\b(query|mutation|subscription)\s+([A-Za-z0-9_]+)/);
  return match?.[2] || 'AnonymousGraphQL';
}

function graphqlWithLog(request: any): Promise<any> {
  const operationName = getOperationName(request?.query);
  const source = 'GraphQLNotificationService';
  const countKey = `${source}:${operationName}`;
  const executionCount = (graphqlExecutionCounts.get(countKey) || 0) + 1;
  graphqlExecutionCounts.set(countKey, executionCount);
  console.log('GRAPHQL:', operationName, { source, executionCount });
  return getClient().graphql(request) as Promise<any>;
}

function invalidateNotificationCache(userId: string): void {
  notificationListCache.delete(userId);
}

// GraphQL queries
const LIST_NOTIFICATIONS_BY_USER = `
  query ListNotificationsByUser($userId: String!, $limit: Int, $nextToken: String) {
    listNotificationsByUser(userId: $userId, limit: $limit, nextToken: $nextToken) {
      items {
        userId
        notificationId
        category
        priority
        title
        message
        icon
        link
        read
        createdAt
        actorName
      }
      nextToken
    }
  }
`;

const GET_UNREAD_COUNT = `
  query GetUnreadCount($userId: String!) {
    getUnreadCount(userId: $userId)
  }
`;

// GraphQL mutations
const CREATE_NOTIFICATION = `
  mutation CreateNotification($input: CreateNotificationInput!) {
    createNotification(input: $input) {
      userId
      notificationId
      category
      priority
      title
      message
      icon
      link
      read
      createdAt
      actorName
    }
  }
`;

const MARK_NOTIFICATION_AS_READ = `
  mutation MarkNotificationAsRead($userId: String!, $notificationId: String!) {
    markNotificationAsRead(userId: $userId, notificationId: $notificationId) {
      userId
      notificationId
      read
    }
  }
`;

const MARK_ALL_NOTIFICATIONS_AS_READ = `
  mutation MarkAllNotificationsAsRead($userId: String!) {
    markAllNotificationsAsRead(userId: $userId)
  }
`;

const DELETE_NOTIFICATION = `
  mutation DeleteNotification($userId: String!, $notificationId: String!) {
    deleteNotification(userId: $userId, notificationId: $notificationId) {
      userId
      notificationId
    }
  }
`;

const CLEAR_ALL_NOTIFICATIONS = `
  mutation ClearAllNotifications($userId: String!) {
    clearAllNotifications(userId: $userId) {
      success
      deletedCount
    }
  }
`;

// Map GraphQL response to AppNotification
const mapToAppNotification = (item: any): AppNotification => ({
  id: item.notificationId,
  category: item.category as NotificationCategory,
  priority: item.priority as NotificationPriority,
  title: item.title,
  message: item.message,
  icon: item.icon,
  link: item.link,
  read: item.read,
  createdAt: item.createdAt,
  recipientId: item.userId,
  actorName: item.actorName,
});

export class GraphQLNotificationService {
  static async getNotifications(userId: string, options: NotificationListOptions = {}): Promise<AppNotification[]> {
    const cached = notificationListCache.get(userId);
    const now = Date.now();

    if (!options.force) {
      if (cached?.promise) {
        console.log('[GraphQLNotificationService] Reusing in-flight notifications request for user:', userId);
        return cached.promise;
      }

      if (cached?.data && now - cached.fetchedAt < NOTIFICATION_LIST_CACHE_TTL_MS) {
        console.log('[GraphQLNotificationService] Using cached notifications for user:', userId);
        return cached.data;
      }
    }

    const request = (async () => {
      try {
        console.log('[GraphQLNotificationService] Getting notifications for user:', userId);
        // userId is the Notifications partition key, so notifications addressed to an
        // employee's old-domain address live in a different partition entirely. Read both
        // and merge, otherwise anyone who switched domains loses their notification
        // history along with their unread badge.
        const rows = await collectAcrossIdentities(userId, async candidate => {
          const response = await graphqlWithLog({
            query: LIST_NOTIFICATIONS_BY_USER,
            variables: { userId: candidate, limit: 100 },
          }) as any;
          if (response.errors && response.errors.length > 0) {
            console.warn('[GraphQLNotificationService] GraphQL errors in getNotifications:', response.errors);
            return [];
          }
          return response.data?.listNotificationsByUser?.items || [];
        }, (item: any) => `${item?.userId}:${item?.notificationId ?? item?.id}`);

        const notifications = rows.map(mapToAppNotification);
        console.log('[GraphQLNotificationService] Retrieved', notifications.length, 'notifications');
        return notifications;
      } catch (error) {
        console.error('[GraphQLNotificationService] Failed to get notifications:', error);
        return cached?.data || [];
      }
    })();

    notificationListCache.set(userId, {
      fetchedAt: cached?.fetchedAt || 0,
      data: cached?.data,
      promise: request,
    });

    const notifications = await request;
    notificationListCache.set(userId, {
      fetchedAt: Date.now(),
      data: notifications,
    });
    return notifications;
  }

  static async getUnreadCount(userId: string): Promise<number> {
    try {
      console.log('[GraphQLNotificationService] Getting unread count for user:', userId);
      // Summed across both domains so the badge matches the list, which now spans both.
      const counts = await collectAcrossIdentities(userId, async candidate => {
        const response = await graphqlWithLog({
          query: GET_UNREAD_COUNT,
          variables: { userId: candidate },
        }) as any;
        if (response.errors && response.errors.length > 0) {
          console.warn('[GraphQLNotificationService] GraphQL errors in getUnreadCount:', response.errors);
          return [0];
        }
        return [response.data?.getUnreadCount || 0];
      }, () => undefined);

      const count = counts.reduce((total: number, value: number) => total + value, 0);
      console.log('[GraphQLNotificationService] Unread count:', count);
      return count;
    } catch (error) {
      console.error('[GraphQLNotificationService] Failed to get unread count:', error);
      // Return 0 instead of throwing - notification count is not critical
      return 0;
    }
  }

  static async createNotification(notification: {
    userId: string;
    category: NotificationCategory;
    priority: NotificationPriority;
    title: string;
    message: string;
    icon: string;
    link?: string;
    actorName?: string;
  }): Promise<AppNotification | null> {
    try {
      console.log('[GraphQLNotificationService] Creating notification for user:', notification.userId);
      const response = await graphqlWithLog({
        query: CREATE_NOTIFICATION,
        variables: { input: notification },
      }) as any;
      
      // Check for GraphQL errors
      if (response.errors && response.errors.length > 0) {
        console.error('[GraphQLNotificationService] GraphQL errors in createNotification:', response.errors);
        return null;
      }
      
      console.log('[GraphQLNotificationService] Notification created successfully');
      invalidateNotificationCache(notification.userId);
      return mapToAppNotification(response.data.createNotification);
    } catch (error) {
      console.error('[GraphQLNotificationService] Failed to create notification:', error);
      return null;
    }
  }

  static async markAsRead(userId: string, notificationId: string): Promise<void> {
    try {
      console.log('[GraphQLNotificationService] Marking notification as read:', notificationId);
      const response = await graphqlWithLog({
        query: MARK_NOTIFICATION_AS_READ,
        variables: { userId, notificationId },
      }) as any;
      
      // Check for GraphQL errors
      if (response.errors && response.errors.length > 0) {
        console.warn('[GraphQLNotificationService] GraphQL errors in markAsRead:', response.errors);
      } else {
        console.log('[GraphQLNotificationService] Notification marked as read');
      }
    } catch (error) {
      console.error('[GraphQLNotificationService] Failed to mark as read:', error);
    } finally {
      invalidateNotificationCache(userId);
    }
  }

  static async markAllAsRead(userId: string): Promise<void> {
    try {
      console.log('[GraphQLNotificationService] Marking all notifications as read for user:', userId);
      const response = await graphqlWithLog({
        query: MARK_ALL_NOTIFICATIONS_AS_READ,
        variables: { userId },
      }) as any;
      
      // Check for GraphQL errors
      if (response.errors && response.errors.length > 0) {
        console.warn('[GraphQLNotificationService] GraphQL errors in markAllAsRead:', response.errors);
      } else {
        console.log('[GraphQLNotificationService] All notifications marked as read');
      }
    } catch (error) {
      console.error('[GraphQLNotificationService] Failed to mark all as read:', error);
    } finally {
      invalidateNotificationCache(userId);
    }
  }

  static async deleteNotification(userId: string, notificationId: string): Promise<void> {
    try {
      console.log('[GraphQLNotificationService] Deleting notification:', notificationId);
      const response = await graphqlWithLog({
        query: DELETE_NOTIFICATION,
        variables: { userId, notificationId },
      }) as any;
      
      // Check for GraphQL errors
      if (response.errors && response.errors.length > 0) {
        console.warn('[GraphQLNotificationService] GraphQL errors in deleteNotification:', response.errors);
      } else {
        console.log('[GraphQLNotificationService] Notification deleted');
      }
    } catch (error) {
      console.error('[GraphQLNotificationService] Failed to delete notification:', error);
    } finally {
      invalidateNotificationCache(userId);
    }
  }

  static async clearAllNotifications(userId: string): Promise<{ success: boolean; errors: string[] }> {
    try {
      console.log('[GraphQLNotificationService] Clearing all notifications for user:', userId);
      invalidateNotificationCache(userId);
      
      // Try the batch clear mutation first
      try {
        const response = await graphqlWithLog({
          query: CLEAR_ALL_NOTIFICATIONS,
          variables: { userId },
        }) as any;
        
        // Check for GraphQL errors
        if (response.errors && response.errors.length > 0) {
          console.warn('[GraphQLNotificationService] GraphQL errors in clearAllNotifications, trying fallback:', response.errors);
          // Fall through to fallback method
        } else {
          const deletedCount = response.data?.clearAllNotifications?.deletedCount || 0;
          console.log('[GraphQLNotificationService] Cleared', deletedCount, 'notifications');
          invalidateNotificationCache(userId);
          return { success: true, errors: [] };
        }
      } catch (batchError) {
        console.warn('[GraphQLNotificationService] Batch clear not available, using fallback method');
      }
      
      // Fallback: Get all notifications and delete them one by one
      const notifications = await this.getNotifications(userId);
      console.log('[GraphQLNotificationService] Deleting', notifications.length, 'notifications individually');
      
      let deleteCount = 0;
      const errors: string[] = [];
      
      for (const notif of notifications) {
        try {
          await this.deleteNotification(userId, notif.id);
          deleteCount++;
        } catch (error: any) {
          errors.push(`Failed to delete ${notif.id}: ${error.message}`);
        }
      }
      
      console.log('[GraphQLNotificationService] Deleted', deleteCount, 'of', notifications.length, 'notifications');
      invalidateNotificationCache(userId);
      return { success: deleteCount > 0, errors };
      
    } catch (error: any) {
      console.error('[GraphQLNotificationService] Failed to clear all notifications:', error);
      invalidateNotificationCache(userId);
      return { success: false, errors: [error.message || 'Failed to clear notifications'] };
    }
  }
}
